import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { isNumber } from 'lodash';
import { usePathname, useRouter } from 'next/navigation';
import {
  state as reactiveState,
  useReactiveState,
} from '@frameio/graphql-client';
import type {
  PaymentCard,
  PlanPeriod,
  PlanQuote,
  PlanQuoteAddressInput,
} from '@frameio/graphql-client/generated';
import {
  useGetAccountBillingQuery,
  useGetPlanQuoteQuery,
  useUpdateSubscriptionAddOnMutation,
  useUpdateSubscriptionMutation,
} from '@frameio/graphql-client/generated';
import { date } from '@frameio/i18n';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { planPeriodKey, planTierKey } from '@frameio/shared/plans/constants';
import {
  isCurrentPlanDiscountOffer,
  useAccountRoleTracking,
  useCurrentPlanTracking,
} from '@frameio/shared/plans/hooks';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import {
  ADOBE_PRIVACY_POLICY_URL,
  ADOBE_TERMS_OF_USE_URL,
  BILLING_URL,
  SUPPORT_URL,
} from '@frameio/utils/constants';
import { formatCents } from '@frameio/utils/formatters/formatters';
import {
  AlertInline,
  Button2,
  DialogActions,
  DialogFooter,
  InlineButton,
  LoadingContainer,
  Spinner,
  Text,
  theme,
  ToastAlert,
  useToast,
} from '@frameio/vapor';
import useUpdatePaymentCard, {
  trackingBillingFields,
} from '../../../billing/hooks/useUpdatePaymentCard';
import {
  segmentTrackingValues,
  SubscriptionUpdateSource,
} from '../../../feature-gate/config';
import { usePlanFragment } from '../../../plans/hooks/usePlanFragment';
import { getIsDueNow } from '../../../plans/utils';
import { StyledLink } from '../../../shared/components/StyledLink';
import { aYearFromNow } from '../../../utils/aYearFromNow';
import { ACCOUNT_CONVERSION_STEPS } from './constants';
import TEXT from './generated/TEXT/PlanSummary';
import { useMaybeHasErrorValidatingVat } from './hooks';
import styles from './PlanSummary.module.css';
import { PlanSummaryDetails } from './PlanSummaryDetails';
import type { ConversionDialogProps } from './types';
import { getIsBillingCountryVatExemptEligible, getIsTaxExempt } from './utils';

const getAddOnCount = (
  addOns: PlanQuote['invoiceLineItems'],
  modifier: string
) =>
  addOns?.find(
    (addOn) =>
      addOn?.item?.__typename === 'LineItem' &&
      addOn?.item?.modifier === modifier
  )?.quantity || 0;

type TextConfirmationDisclaimer = {
  isBillingCountryVatExemptEligible?: boolean;
  isDiscountPlan?: boolean;
  isDueNow: boolean;
  isPausedPlan?: boolean;
  isTaxExempt: boolean;
  isTrialActive: boolean;
  period: PlanPeriod;
  pauseOfferDurationMonths?: number;
  tax: number;
  total: number;
  offerEndDate: Date;
};

TEXT.DEFINE(
  {
    /** Button text for freezing account */
    agreeAndFreeze: 'Agree & Freeze',
    /** Button text for subscribing */
    agreeAndSubscribe: 'Agree & Subscribe',
    /** Accessibility labels */
    ariaLabels: {
      /** Label for submission spinner */
      spinner: 'Submitting',
      /** Label for payment error dismissal */
      dismissPaymentError: 'Dismiss payment error',
    },
    /** Cancel button text */
    cancel: 'Cancel',
    /** Back button text */
    back: 'Back',
    /** Disclaimer when canceling current plan */
    cancelDisclaimer:
      'Your current plan will be cancelled and your new plan will begin immediately.',
    /** Disclaimer for due now amount with prorated credit */
    dueNowDisclaimer: () => (
      <>
        *Due now amount based on when change is processed. A prorated credit for
        unused time on your existing subscription will be applied. Detailed
        receipt available on{' '}
        <StyledLink href={BILLING_URL} target={'_blank'}>
          Billing page.
        </StyledLink>
      </>
    ),
    /** Discount plan - monthly period - Adobe terms */
    confirmationDisclaimerDiscountMonthlyAdobe: (
      formattedTotal: string,
      offerEndDate: Date
    ) => (
      <>
        By clicking "Agree & Subscribe", you agree: You will be charged{' '}
        {formattedTotal} monthly for the first year. After your offer term ends
        on {date(offerEndDate, 'medium')}, you will be charged the then-current
        price. At the end of your one-year term, your subscription will
        automatically renew monthly until you cancel. No annual commitment
        required after the first year. Price subject to change at renewal.
        Cancel anytime via{' '}
        <StyledLink href={BILLING_URL} target={'_blank'}>
          Account Settings
        </StyledLink>{' '}
        or{' '}
        <StyledLink href={SUPPORT_URL} target={'_blank'}>
          Customer Support
        </StyledLink>
        . You also agree to the Adobe{' '}
        <StyledLink href={ADOBE_TERMS_OF_USE_URL} target={'_blank'}>
          Terms of Use
        </StyledLink>{' '}
        and{' '}
        <StyledLink href={ADOBE_PRIVACY_POLICY_URL} target={'_blank'}>
          Privacy Policy
        </StyledLink>
        .
      </>
    ),

    /** Discount plan - yearly period - Adobe terms */
    confirmationDisclaimerDiscountYearlyAdobe: (
      formattedTotal: string,
      offerEndDate: Date
    ) => (
      <>
        By clicking "Agree & Subscribe", you agree: You will be charged{' '}
        {formattedTotal} for the first year. After your offer term ends on{' '}
        {date(offerEndDate, 'medium')}, you will be charged the then-current
        price annually. Your subscription will automatically renew annually
        until you cancel. Price subject to change at renewal. Cancel anytime via{' '}
        <StyledLink href={BILLING_URL} target={'_blank'}>
          Account Settings
        </StyledLink>{' '}
        or{' '}
        <StyledLink href={SUPPORT_URL} target={'_blank'}>
          Customer Support
        </StyledLink>
        . You also agree to the Adobe{' '}
        <StyledLink href={ADOBE_TERMS_OF_USE_URL} target={'_blank'}>
          Terms of Use
        </StyledLink>{' '}
        and{' '}
        <StyledLink href={ADOBE_PRIVACY_POLICY_URL} target={'_blank'}>
          Privacy Policy
        </StyledLink>
        .
      </>
    ),

    /** Paused plan - Adobe terms */
    confirmationDisclaimerPausedAdobe: (
      formattedTotal: string,
      pauseOfferDurationMonths: number
    ) => (
      <>
        By selecting “Agree and Freeze”, your account will be frozen
        immediately, and you will be charged {formattedTotal} (+tax) until you
        reactivate your plan, for up to {pauseOfferDurationMonths} months or
        until you cancel. If still frozen after {pauseOfferDurationMonths}{' '}
        months, your account will automatically reactivate, and you will be
        charged the then-current price. Cancel anytime via{' '}
        <StyledLink href={BILLING_URL} target={'_blank'}>
          Account Settings
        </StyledLink>{' '}
        or{' '}
        <StyledLink href={SUPPORT_URL} target={'_blank'}>
          Customer Support
        </StyledLink>
        . You also agree to the Adobe{' '}
        <StyledLink href={ADOBE_TERMS_OF_USE_URL} target={'_blank'}>
          Terms of Use
        </StyledLink>
        .
      </>
    ),

    /** Regular subscription - trial active - Adobe terms */
    confirmationDisclaimerRegularTrialAdobe: (
      formattedTotal: string,
      taxInfo: string,
      period: string
    ) => (
      <>
        By clicking “Agree & Subscribe”, you agree: After the free trial ends,
        you will be charged {formattedTotal} {taxInfo} {period}. Your
        subscription will automatically renew {period} until you cancel (price
        subject to change). Cancel anytime via{' '}
        <StyledLink href={BILLING_URL} target={'_blank'}>
          Account Settings
        </StyledLink>{' '}
        or{' '}
        <StyledLink href={SUPPORT_URL} target={'_blank'}>
          Customer Support
        </StyledLink>
        . You also agree to the Adobe{' '}
        <StyledLink href={ADOBE_TERMS_OF_USE_URL} target={'_blank'}>
          Terms of Use
        </StyledLink>{' '}
        and{' '}
        <StyledLink href={ADOBE_PRIVACY_POLICY_URL} target={'_blank'}>
          Privacy Policy
        </StyledLink>
        .
      </>
    ),

    /** Regular subscription - no trial - Adobe terms */
    confirmationDisclaimerRegularNoTrialAdobe: (
      formattedTotal: string,
      taxInfo: string,
      period: string
    ) => (
      <>
        By clicking “Agree & Subscribe”, you agree: You will be charged{' '}
        {formattedTotal} {taxInfo} {period}. Your subscription will
        automatically renew {period} until you cancel (price subject to change).
        Cancel anytime via{' '}
        <StyledLink href={BILLING_URL} target={'_blank'}>
          Account Settings
        </StyledLink>{' '}
        or{' '}
        <StyledLink href={SUPPORT_URL} target={'_blank'}>
          Customer Support
        </StyledLink>
        . You also agree to the Adobe{' '}
        <StyledLink href={ADOBE_TERMS_OF_USE_URL} target={'_blank'}>
          Terms of Use
        </StyledLink>{' '}
        and{' '}
        <StyledLink href={ADOBE_PRIVACY_POLICY_URL} target={'_blank'}>
          Privacy Policy
        </StyledLink>
        .
      </>
    ),

    /** Error messages */
    error: {
      /** Payment declined error message */
      payment:
        'Payment declined. Update payment details in Settings and try again.',
    },
    /** Toast message details */
    toastDetails: "We've emailed a receipt to the account owner.",
    /** Toast title for frozen account */
    onUpdateSubscriptionFrozen: "You've frozen your account",
    /** Toast title for successful subscription */
    onUpdateSubscriptionSuccess: (title: string) =>
      `You've subscribed to the ${title} plan.`,
    /** Payment updated success message */
    paymentUpdated: 'Your payment information has been updated.',
    /** Settings navigation text */
    settings: ' Settings',
    /** Loading indicator label */
    loadingLabel: 'Loading content...',
  },
  {
    StyledLink,
    BILLING_URL,
    SUPPORT_URL,
    ADOBE_TERMS_OF_USE_URL,
    ADOBE_PRIVACY_POLICY_URL,
  }
);

/** Helper function to build confirmation disclaimer based on conditions */
const getConfirmationDisclaimer = ({
  isBillingCountryVatExemptEligible,
  isDiscountPlan,
  isDueNow,
  isPausedPlan,
  isTaxExempt,
  isTrialActive,
  pauseOfferDurationMonths,
  period,
  tax,
  total,
  offerEndDate,
}: TextConfirmationDisclaimer) => {
  // Format total BEFORE passing to TEXT functions
  const formattedTotal = formatCents(total, true);

  // Calculate tax information string
  let taxInfo = '';
  if (isBillingCountryVatExemptEligible) {
    taxInfo = tax === 0 ? '(excl. VAT)' : '(incl. VAT)';
  } else if (isTaxExempt) {
    taxInfo = '(excl. tax)';
  }

  const periodLower = period.toLowerCase();

  // Discount plan
  if (isDiscountPlan) {
    if (period === 'MONTHLY') {
      return TEXT.confirmationDisclaimerDiscountMonthlyAdobe(
        formattedTotal,
        offerEndDate
      );
    }
    return TEXT.confirmationDisclaimerDiscountYearlyAdobe(
      formattedTotal,
      offerEndDate
    );
  }

  // Paused plan
  if (isPausedPlan) {
    return TEXT.confirmationDisclaimerPausedAdobe(
      formattedTotal,
      pauseOfferDurationMonths ?? 0
    );
  }

  // Regular subscription
  const showTrialText = isTrialActive && !isDueNow;
  if (showTrialText) {
    return TEXT.confirmationDisclaimerRegularTrialAdobe(
      formattedTotal,
      taxInfo,
      periodLower
    );
  }

  return TEXT.confirmationDisclaimerRegularNoTrialAdobe(
    formattedTotal,
    taxInfo,
    periodLower
  );
};

export type PlanQuoteAddOns = {
  quantity: number;
  id: string;
  actionDescription: string;
};

export const PlanSummary = ({
  feature,
  featureGateType,
  onCancel,
  period,
  planId,
  planQuoteAddOns,
  setParentStep,
  shouldUpgradeSubscriptionImmediately,
  subscriptionUpdateSource,
  title,
  usageLimitExceeded,
  userLimit,
  paymentMethod,
  isDiscountPlan,
  isPausedPlan,
  pauseOfferDurationMonths,
}: {
  onCancel: (shouldTrackExit?: boolean) => void;
  planQuoteAddOns: PlanQuoteAddOns | null;
  planId: string;
  setParentStep: Dispatch<SetStateAction<ACCOUNT_CONVERSION_STEPS>>;
  paymentMethod?: PaymentCard;
} & Pick<
  ConversionDialogProps,
  | 'feature'
  | 'featureGateType'
  | 'period'
  | 'shouldUpgradeSubscriptionImmediately'
  | 'subscriptionUpdateSource'
  | 'title'
  | 'usageLimitExceeded'
  | 'userLimit'
  | 'isDiscountPlan'
  | 'isPausedPlan'
  | 'pauseOfferDurationMonths'
>) => {
  // Extract ALL TEXT properties at top level to avoid hooks violations
  const toastDetails = TEXT.toastDetails;
  const onUpdateSubscriptionFrozen = TEXT.onUpdateSubscriptionFrozen;
  const paymentUpdated = TEXT.paymentUpdated;
  const loadingLabel = TEXT.loadingLabel;
  const cancelDisclaimer = TEXT.cancelDisclaimer;
  const dueNowDisclaimer = TEXT.dueNowDisclaimer;
  const settings = TEXT.settings;
  const dismissPaymentError = TEXT.ariaLabels.dismissPaymentError;
  const errorPayment = TEXT.error.payment;
  const back = TEXT.back;
  const cancel = TEXT.cancel;
  const spinnerLabel = TEXT.ariaLabels.spinner;
  const agreeAndFreeze = TEXT.agreeAndFreeze;
  const agreeAndSubscribe = TEXT.agreeAndSubscribe;

  const accountId = useActiveAccountId() ?? '';
  const pathName = usePathname();
  const router = useRouter();

  const { addToast } = useToast();
  const hasPlanQuoteAddOns = !!planQuoteAddOns;

  const handleUpdateSubscriptionAddOnCompleted = () => {
    const toastTitle = planQuoteAddOns?.actionDescription;

    onCancel(false);
    addToast(
      (close, closeLabel) => (
        <ToastAlert
          closeLabel={closeLabel}
          details={toastDetails}
          onRequestClose={close}
          title={toastTitle ?? ''}
          variant="success"
        />
      ),
      { duration: 3000 }
    );
  };

  const handleUpdateSubscriptionCompleted = () => {
    const toastTitle = (
      isPausedPlan
        ? onUpdateSubscriptionFrozen
        : TEXT.onUpdateSubscriptionSuccess(title)
    ) as string;

    addToast(
      (close, closeLabel) => (
        <ToastAlert
          closeLabel={closeLabel}
          details={toastDetails}
          onRequestClose={close}
          title={toastTitle}
          variant="success"
        />
      ),
      { duration: 3000 }
    );

    if (
      isTrialActive &&
      subscriptionUpdateSource === SubscriptionUpdateSource.STORAGE_ADDONS
    ) {
      setParentStep(ACCOUNT_CONVERSION_STEPS.MANAGE_STORAGE);
      return;
    }

    onCancel(false);
  };

  const [hasSubscriptionError, setHasSubscriptionError] =
    useState<boolean>(false);
  const handleUpdateSubscriptionError = () => {
    setHasSubscriptionError(true);
  };

  const { data: subscriptionData, loading: loadingSubscriptionData } =
    useGetAccountBillingQuery({
      variables: {
        accountId,
      },
      skip: !accountId,
    });

  const cachedStripeData = useReactiveState(reactiveState.billing.stripe);

  const cachedAddressRef = useRef(
    cachedStripeData?.addressElementValue?.address
  );
  let addressInput: PlanQuoteAddressInput | undefined;

  if (cachedAddressRef.current) {
    addressInput = {
      city: cachedAddressRef.current.city,
      country: cachedAddressRef.current.country,
      line1: cachedAddressRef.current.line1,
      line2: cachedAddressRef.current.line2,
      postalCode: cachedAddressRef.current.postal_code,
      state: cachedAddressRef.current.state,
    };
  }

  const { data: planQuotedData, loading: loadingPlanQuote } =
    useGetPlanQuoteQuery({
      fetchPolicy: 'no-cache',
      variables: {
        accountId,
        address: addressInput,
        purchasePlanId: planId,
        ...(hasPlanQuoteAddOns && {
          addOns: [
            { id: planQuoteAddOns.id, quantity: planQuoteAddOns.quantity },
          ],
        }),
      },
      skip: !accountId,
    });

  const subscription = subscriptionData?.account?.subscription;
  const nextBillAt = subscription?.nextBillAt;

  const planQuote = planQuotedData?.account?.subscription
    ?.planQuote as PlanQuote;
  const total = planQuote?.total;
  const isTrialActive = !!subscription?.trial?.isActive;
  const promotionExpiresAt = subscription?.trial?.promotionExpiresAt;
  const isCurrentUserSubscriptionFree = subscription?.tier === 'FREE';

  const tax = planQuote?.tax ?? 0;
  const taxExempt = planQuote?.taxExempt;
  const isTaxExempt = taxExempt ? getIsTaxExempt(taxExempt) : false;

  const isDueNow = getIsDueNow(
    !!shouldUpgradeSubscriptionImmediately,
    nextBillAt
  );

  const [updateSubscription, { loading: loadingSubscriptionUpdate }] =
    useUpdateSubscriptionMutation({
      variables: {
        input: {
          accountId,
          planId,
        },
      },
      /**
       *  We need to refetch GetAccountUsage because the archival storage values
       *  are updated asynchronously through an Oban job in massdriver. This is
       *  an issue when switching from a plan with archival storage to one without.
       **/
      refetchQueries: ['GetAccountUsage', 'GetFeatureGateSuggestions'],
      update(cache, { data }) {
        // the subscription has changed to a new plan
        // evict trial data from the cache as it is for the previous subscription
        const subscriptionId = data?.updateSubscriptionPlan?.subscription.id;

        if (subscriptionId) {
          cache.evict({
            id: `AccountSubscription:${subscriptionId}`,
            fieldName: 'trial',
          });
          cache.gc();
        }
      },
      onQueryUpdated(observableQuery) {
        // do an additional refetch of GetAccountUsage to ensure the archival storage values are updated
        if (observableQuery.queryName === 'GetAccountUsage') {
          return observableQuery.refetch();
        }

        // Listing a query in refetchQueries only passes it to onQueryUpdated;
        // returning false here would skip it, so refetch GetFeatureGateSuggestions
        // explicitly so gated UI reflects the new plan.
        if (observableQuery.queryName === 'GetFeatureGateSuggestions') {
          return observableQuery.refetch();
        }

        return false;
      },

      onCompleted: handleUpdateSubscriptionCompleted,
      onError: handleUpdateSubscriptionError,
    });

  const [
    updatePaymentCard,
    { loading: loadingUpdatePaymentCard, error: updatePaymentCardError },
  ] = useUpdatePaymentCard(() => {
    reactiveState.billing.stripe.set({});
    if (shouldUpgradeSubscriptionImmediately) {
      updateSubscription();
    } else {
      /**
       * We do not fire an update subscription mutation immediately here.
       * We are simply displaying to the end user that a successful confirmation of action.
       */
      addToast(
        (close, closeLabel) => (
          <ToastAlert
            closeLabel={closeLabel}
            onRequestClose={close}
            title={paymentUpdated}
            variant="success"
          />
        ),
        { duration: 3000 }
      );
      onCancel(false);
    }
  });

  const hasUpdatePaymentCardError = !!updatePaymentCardError;

  const [updateSubscriptionAddOn, { loading: loadingSubscriptionAddOnUpdate }] =
    useUpdateSubscriptionAddOnMutation({
      variables: {
        input: {
          quantity: planQuoteAddOns?.quantity ?? 0,
          subscriptionAddOnId: planQuoteAddOns?.id ?? '',
        },
      },
      refetchQueries: ['GetAccountUsage'],
      onCompleted: handleUpdateSubscriptionAddOnCompleted,
    });

  const { data: selectedPlanData } = usePlanFragment(planId);

  const currentPlanTracking = useCurrentPlanTracking();
  const accountRoleTracking = useAccountRoleTracking();

  const track = useTrackUnsafe();

  const handleOnClick = useCallback(() => {
    const addOns = planQuote?.invoiceLineItems;
    const seatAddOnCount = getAddOnCount(addOns, 'user_limit');
    const storageAddOnCount = getAddOnCount(addOns, 'storage_limit');
    const mountedStorageAddOnCount = getAddOnCount(
      addOns,
      'mounted_storage_limit'
    );

    const subscriptionSubtotal = (planQuote?.subtotal ?? 0) / 100;

    const hasCachedCard = !!(
      cachedStripeData?.addressElementValue && cachedStripeData?.stripeTokenId
    );

    track('subscription-terms-accepted', {
      ...currentPlanTracking,
      ...accountRoleTracking,
      feature: feature ? segmentTrackingValues.features[feature] : undefined,
      feature_gate_type: featureGateType,
      selected_plan_cost: selectedPlanData.cost,
      selected_plan_id: selectedPlanData.id,
      selected_plan_is_offer: isCurrentPlanDiscountOffer(selectedPlanData.name),
      selected_plan_is_trial: false,
      selected_plan_name: selectedPlanData.name,
      selected_plan_period:
        selectedPlanData.period && planPeriodKey[selectedPlanData.period],
      selected_plan_tier:
        selectedPlanData.tier && planTierKey[selectedPlanData.tier],
      selected_plan_version: selectedPlanData.version,
      selected_seat_add_on_count: seatAddOnCount || 0,
      selected_storage_add_on_count: storageAddOnCount || 0,
      selected_mounted_storage_add_on_count: mountedStorageAddOnCount || 0,
      selected_subscription_subtotal: subscriptionSubtotal,
      subscription_update_source: subscriptionUpdateSource,
      usage_limit_exceeded: usageLimitExceeded,
    });

    if (hasPlanQuoteAddOns) {
      updateSubscriptionAddOn();
      return;
    }
    if (hasCachedCard) {
      updatePaymentCard({
        addressElementValue: cachedStripeData.addressElementValue,
        stripeTokenId: cachedStripeData.stripeTokenId,
        subscriptionUpdateSource,
        trackingBillingFieldsUpdated:
          trackingBillingFields.paymentMethodAndBillingAddress,
      });
    }
    if (!hasCachedCard && shouldUpgradeSubscriptionImmediately) {
      updateSubscription();
    }
  }, [
    cachedStripeData,
    currentPlanTracking,
    accountRoleTracking,
    feature,
    featureGateType,
    hasPlanQuoteAddOns,
    planQuote,
    selectedPlanData,
    shouldUpgradeSubscriptionImmediately,
    subscriptionUpdateSource,
    track,
    updatePaymentCard,
    updateSubscription,
    updateSubscriptionAddOn,
    usageLimitExceeded,
  ]);

  const isManagingAddOns = !!planQuoteAddOns;

  const showDueNowDisclaimer =
    isDueNow && !isCurrentUserSubscriptionFree && !isTrialActive;

  const showCancelDisclaimer = showDueNowDisclaimer && !isManagingAddOns;

  const isLoading = loadingSubscriptionData || loadingPlanQuote;

  const hasError = hasSubscriptionError || hasUpdatePaymentCardError;

  const showButtonSpinner =
    loadingSubscriptionUpdate ||
    loadingUpdatePaymentCard ||
    loadingSubscriptionAddOnUpdate;

  const isDisabled = showButtonSpinner || hasError;

  const handleOnSettingsClick = useCallback(() => {
    if (pathName === BILLING_URL) {
      onCancel();
    } else {
      onCancel();
      router.push(BILLING_URL);
    }
  }, [onCancel, pathName, router]);

  const billingCountry = cachedAddressRef?.current?.country;

  const maybeHasErrorValidatingVat = useMaybeHasErrorValidatingVat(tax);

  const isBillingCountryVatExemptEligible = billingCountry
    ? getIsBillingCountryVatExemptEligible(billingCountry)
    : false;

  const hasPaymentMethod = !!paymentMethod?.card;

  const renderBackButton =
    hasPlanQuoteAddOns ||
    ((maybeHasErrorValidatingVat ||
      (isBillingCountryVatExemptEligible && tax > 0)) &&
      !hasPaymentMethod);

  const handleOnGoBack = useCallback(() => {
    setParentStep(
      hasPlanQuoteAddOns
        ? ACCOUNT_CONVERSION_STEPS.MANAGE_STORAGE
        : ACCOUNT_CONVERSION_STEPS.PAYMENT
    );
  }, [setParentStep, hasPlanQuoteAddOns]);

  return (
    <LoadingContainer
      isLoading={isLoading}
      loadingLabel={loadingLabel}
      style={{ minHeight: '430px', height: 'auto' }}
    >
      <div className={styles.container}>
        <>
          <PlanSummaryDetails
            isCurrentUserSubscriptionFree={isCurrentUserSubscriptionFree}
            isPausedPlan={isPausedPlan}
            isTrialActive={isTrialActive}
            nextBillAt={nextBillAt}
            period={period}
            planQuote={planQuote}
            promotionExpiresAt={promotionExpiresAt}
            shouldUpgradeSubscriptionImmediately={
              shouldUpgradeSubscriptionImmediately
            }
            title={title}
            userLimit={userLimit}
          />

          <div className={styles.disclaimerContainer}>
            {showCancelDisclaimer && (
              <Text color="primary" variant="caption">
                {cancelDisclaimer}
              </Text>
            )}

            {showDueNowDisclaimer && (
              <Text color="tertiary" variant="caption">
                {dueNowDisclaimer()}
              </Text>
            )}

            {isNumber(total) && (
              <Text color="tertiary" variant="caption">
                {getConfirmationDisclaimer({
                  isBillingCountryVatExemptEligible,
                  isDiscountPlan,
                  isDueNow,
                  isPausedPlan,
                  isTaxExempt,
                  isTrialActive,
                  pauseOfferDurationMonths,
                  period,
                  tax,
                  total,
                  offerEndDate: new Date(aYearFromNow),
                })}
              </Text>
            )}
          </div>
        </>
      </div>

      <AnimatePresence>
        {hasError && (
          <motion.div
            animate={{
              height: 'auto',
              opacity: 1,
            }}
            className={styles.alertInlineContainer}
            exit={{
              height: 0,
              opacity: 0,
            }}
            initial={{
              height: 0,
              opacity: 0,
            }}
            transition={{
              duration: 0.175,
              easings: theme.transitions.eases.primary,
            }}
          >
            <AlertInline
              adornmentAfter={
                <InlineButton onPress={handleOnSettingsClick} variant="primary">
                  {settings}
                </InlineButton>
              }
              closeButtonAccessibilityLabel={dismissPaymentError}
              onRequestClose={() => setHasSubscriptionError(false)}
              variant="error"
            >
              {errorPayment}
            </AlertInline>
          </motion.div>
        )}
      </AnimatePresence>

      <DialogFooter>
        <DialogActions>
          {renderBackButton ? (
            <Button2 onPress={handleOnGoBack} tabIndex={0}>
              {back}
            </Button2>
          ) : (
            <Button2 onPress={() => onCancel()} tabIndex={0}>
              {cancel}
            </Button2>
          )}

          <Button2
            data-testid="plan-summary-confirmation-button"
            isDisabled={isDisabled}
            onPress={handleOnClick}
            tabIndex={0}
            variant="primary"
          >
            <AnimatePresence mode="sync">
              {showButtonSpinner && (
                <motion.div
                  animate={{
                    opacity: 1,
                  }}
                  exit={{
                    opacity: 0,
                  }}
                  initial={{
                    opacity: 0,
                  }}
                  style={{
                    left: '50%',
                    position: 'absolute',
                    top: '30%',
                    transform: 'translate(-50%, -50%)',
                  }}
                  transition={{
                    duration: 0.2,
                    easings: theme.transitions.eases.primary,
                  }}
                >
                  <Spinner
                    aria-label={spinnerLabel}
                    delay={0}
                    isLoading={true}
                    size="x-small"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div
              aria-live="assertive"
              role="status"
              style={{
                border: 0,
                clip: 'rect(0 0 0 0)',
                height: '1px',
                margin: '-1px',
                overflow: 'hidden',
                padding: 0,
                position: 'absolute',
                width: '1px',
              }}
            >
              {showButtonSpinner ? spinnerLabel : null}
            </div>

            <motion.div animate={{ opacity: showButtonSpinner ? 0 : 1 }}>
              {isPausedPlan ? agreeAndFreeze : agreeAndSubscribe}
            </motion.div>
          </Button2>
        </DialogActions>
      </DialogFooter>
    </LoadingContainer>
  );
};
